import type { AdsAuthorisationAdvertiser, TikTokRegion } from "@unbox/api";
import { useState } from "react";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { cx } from "@/utils/cx";
import { regionName } from "@/utils/regions";

export type AdvertiserOption = AdsAuthorisationAdvertiser & {
  displayName: string;
  regionCode: string;
  isBestMatch: boolean;
};

export type AdvertiserRegionGroup = {
  regionCode: string;
  regionName: string;
  options: AdvertiserOption[];
};

const TIKTOK_REGION_BY_COUNTRY: Partial<Record<string, TikTokRegion>> = { GB: "UK" };

const UNKNOWN_REGION = "—";

export const toRegionCode = (country: string | null | undefined): string =>
  country ? (TIKTOK_REGION_BY_COUNTRY[country] ?? country) : UNKNOWN_REGION;

export const toDisplayName = (advertiser: AdsAuthorisationAdvertiser): string =>
  advertiser.name ? advertiser.name.replace(/_+/g, " ").trim() : `Advertiser ${advertiser.advertiserId}`;

const matchScore = (displayName: string, merchantName: string): number => {
  const haystack = displayName.toLowerCase();
  const words = merchantName
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 2);
  if (words.length === 0) return 0;
  return words.reduce((score, word) => (haystack.includes(word) ? score + word.length : score), 0);
};

export const buildAdvertiserGroups = (
  advertisers: AdsAuthorisationAdvertiser[],
  merchantName: string,
  merchantRegion: string | null,
): AdvertiserRegionGroup[] => {
  const merchantRegionCode = merchantRegion ? toRegionCode(merchantRegion) : null;

  const scored = advertisers.map((advertiser) => {
    const displayName = toDisplayName(advertiser);
    const regionCode = toRegionCode(advertiser.country);
    const regionBonus = merchantRegionCode && regionCode === merchantRegionCode ? 1 : 0;
    return { advertiser, displayName, regionCode, score: matchScore(displayName, merchantName) + regionBonus };
  });

  const best = scored.reduce<(typeof scored)[number] | null>(
    (winner, row) => (row.score > 0 && (!winner || row.score > winner.score) ? row : winner),
    null,
  );

  const options: AdvertiserOption[] = scored.map((row) => ({
    ...row.advertiser,
    displayName: row.displayName,
    regionCode: row.regionCode,
    isBestMatch: !!best && row.advertiser.advertiserId === best.advertiser.advertiserId,
  }));

  const bestRegion = best?.regionCode ?? merchantRegionCode;
  const regionCodes = [...new Set(options.map((option) => option.regionCode))].sort((a, b) => {
    if (a === bestRegion) return -1;
    if (b === bestRegion) return 1;
    return a.localeCompare(b);
  });

  return regionCodes.map((regionCode) => ({
    regionCode,
    regionName: regionName(regionCode),
    options: options
      .filter((option) => option.regionCode === regionCode)
      .sort((a, b) => {
        if (a.isBestMatch) return -1;
        if (b.isBestMatch) return 1;
        return a.displayName.localeCompare(b.displayName);
      }),
  }));
};

type AdvertiserPickerProps = {
  groups: AdvertiserRegionGroup[];
  selectedId: string | null;
  onSelect: (advertiserId: string) => void;
};

export const AdvertiserPicker = ({ groups, selectedId, onSelect }: AdvertiserPickerProps) => {
  const [showAll, setShowAll] = useState(false);

  const total = groups.reduce((count, group) => count + group.options.length, 0);
  const otherMarkets = total - (groups[0]?.options.length ?? 0);
  const visible = showAll ? groups : groups.slice(0, 1);

  return (
    <div className="mt-6 flex flex-col gap-5">
      {visible.map((group) => (
        <div key={group.regionCode}>
          <h2 className="m-0 text-xs font-semibold uppercase tracking-wide text-tertiary">
            {group.regionName} · {group.regionCode}
          </h2>
          <ul className="mt-2 flex list-none flex-col gap-2 p-0">
            {group.options.map((option) => {
              const isSelected = option.advertiserId === selectedId;
              return (
                <li key={option.advertiserId}>
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onSelect(option.advertiserId)}
                    className={cx(
                      "flex w-full cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors duration-100",
                      isSelected ? "border-brand bg-brand-primary" : "border-secondary bg-primary hover:border-primary",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cx(
                        "inline-flex size-4 shrink-0 items-center justify-center rounded-full border",
                        isSelected ? "border-brand" : "border-secondary",
                      )}
                    >
                      {isSelected && <span className="size-2 rounded-full bg-brand-solid" />}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-primary">{option.displayName}</span>
                        {option.isBestMatch && (
                          <BadgeWithDot color="brand" className="shrink-0">
                            Best match
                          </BadgeWithDot>
                        )}
                      </span>
                      <span className="truncate text-xs text-tertiary">
                        Advertiser ID {option.advertiserId} · {option.regionCode}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {!showAll && otherMarkets > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="cursor-pointer self-start border-0 bg-transparent p-0 text-sm font-medium text-brand-secondary hover:underline"
        >
          Show all {total} accounts ({otherMarkets} in other markets)
        </button>
      )}
    </div>
  );
};
